import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, ViewContainerRef, Component, ViewChild, HostListener, NgModule } from '@angular/core';
import * as i3 from '@upr-web-primer/lock-scroll';
import * as i5 from '@upr-web-primer/storage';
import * as i4 from '@upr-web-primer/viewport';
import { WINDOW } from '@upr-web-primer/window';
import { Subject, Subscription, BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import * as i6 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';

// Angular
/**
 * Export class SimpleModalBroadcaster
 */
const _c0 = ["container"];
function SimpleModalComponent_div_0_div_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 5);
  }
}
function SimpleModalComponent_div_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2)(1, "div", 3);
    i0.ɵɵelement(2, "span", null, 0);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, SimpleModalComponent_div_0_div_4_Template, 1, 0, "div", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngStyle", ctx_r0.parentCss);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngStyle", ctx_r0.childCss);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngIf", !ctx_r0.isHidden && ctx_r0.isShade);
  }
}
class SimpleModalBroadcaster {
  constructor() {
    /**
     * Event emitter
     * @type Subject<IBroadCaster>
     */
    this._eventBus = new Subject();
  }
  /**
   * Emit data to the specific event
   * @param {string} key
   * @param {any} data
   * @return void
   */
  emit(key, data) {
    this._eventBus.next({
      key,
      data
    });
  }
  /**
   * Subscribe to the new event
   * @param {string} key
   * @return {Observable<T>}
   */
  on(key) {
    return this._eventBus.asObservable().pipe(filter(event => event.key === key), map(event => event.data));
  }
  static {
    this.ɵfac = function SimpleModalBroadcaster_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SimpleModalBroadcaster)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: SimpleModalBroadcaster,
      factory: SimpleModalBroadcaster.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SimpleModalBroadcaster, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

// Angular
/**
 * Export SimpleModalHelper
 */
class SimpleModalHelper {
  /**
   * Constructor
   * @param {ComponentFactoryResolver} componentResolver
   * @param {ApplicationRef} appRef
   * @param {Injector} injector
   * @param {any} document
   */
  constructor(componentRefs, document, componentResolver, appRef, injector) {
    this.componentRefs = componentRefs;
    this.document = document;
    this.componentResolver = componentResolver;
    this.appRef = appRef;
    this.injector = injector;
  }
  /**
   * Dispatch for code
   * @param {any} event
   * @return {string|number}
   */
  dispatchForCode(event) {
    let code;
    if (event.key !== undefined) {
      code = event.key;
    } else if (event.keyIdentifier !== undefined) {
      code = event.keyIdentifier;
    } else if (event.keyCode !== undefined) {
      code = event.keyCode;
    }
    return code;
  }
  /**
   * Generate random id
   * @return {string}
   */
  generateId() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }
  /**
   * Create component dynamically
   * @param component
   * @param {object} data
   * @param {ViewContainerRef} container
   * @return {ComponentRef<any>}
   */
  createComponent(component, data, container) {
    // Define component variable
    let componentRef;
    // Get component instance from factory resolver
    const getComponent = componentName => {
      if (this.componentRefs) {
        const firstRef = this.componentRefs[componentName];
        if (firstRef) {
          return firstRef;
        }
      }
      // @ts-ignore "_factories" is private property, that's why we need ts-ignore in here, don't remove it, it would not reflect on anything
      const factories = Array.from(this.componentResolver['_factories'].keys());
      return factories.find(x => {
        const matches = /function (.*?)\(.*/g.exec(x + '');
        return matches && matches[1] && matches[1].toLowerCase() === componentName.toLowerCase();
      });
    };
    // Pass data to the component
    const passData = ref => {
      // If we got params
      if (data) {
        for (const x in data) {
          if (data.hasOwnProperty(x)) {
            ref.instance[x] = data[x];
          }
        }
      }
    };
    // Get component factory
    const factory = this.componentResolver.resolveComponentFactory(typeof component === 'string' ? getComponent(component) : component);
    // If container already specified
    if (!container) {
      // Create component instance
      componentRef = factory.create(this.injector);
      // Pass the data to the component
      passData(componentRef);
      // Attach component to the appRef so that it's inside the ng component tree
      this.appRef.attachView(componentRef.hostView);
      // Append component to the body
      this.document.body.appendChild(componentRef.hostView.rootNodes[0]);
      // We should add a new component to the <body>
    } else {
      // Create component instance
      componentRef = container.createComponent(factory);
      // Pass the data to the component
      passData(componentRef);
      // Detect changes
      componentRef.changeDetectorRef.detectChanges();
    }
    // Return references
    return componentRef;
  }
  /**
   * Get object sizeof
   * @param {IKeyValue} obj
   * @return {number}
   */
  sizeOf(obj) {
    let size = 0,
      key;
    for (key in obj) {
      if (obj.hasOwnProperty(key)) {
        size++;
      }
    }
    return size;
  }
  /**
   * Merge objects|array
   * @param {any} args
   * @return {IKeyValue}
   */
  merge(...args) {
    const obj = {};
    args.forEach(val => {
      for (const x in val) {
        if (val.hasOwnProperty(x)) {
          obj[x] = val[x];
        }
      }
    });
    return obj;
  }
  /**
   * Get modal id by component name
   * @param {any} component
   * @return {string}
   */
  getId(component) {
    return (component + '').toLowerCase();
  }
  static {
    this.ɵfac = function SimpleModalHelper_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SimpleModalHelper)(i0.ɵɵinject('ComponentRefs', 8), i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(i0.ComponentFactoryResolver), i0.ɵɵinject(i0.ApplicationRef), i0.ɵɵinject(i0.Injector));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: SimpleModalHelper,
      factory: SimpleModalHelper.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SimpleModalHelper, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: ['ComponentRefs']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }, {
    type: i0.ComponentFactoryResolver
  }, {
    type: i0.ApplicationRef
  }, {
    type: i0.Injector
  }], null);
})();

/**
 * Export KEYS
 * @type {{}}
 */
const SimpleModalKeys = {
  OVERRIDE: 'OVERRIDE',
  OPEN_MODAL: 'OPEN_MODAL',
  CLOSE_MODAL: 'CLOSE_MODAL',
  HIDE_MODAL: 'HIDE_MODAL',
  SHOW_MODAL: 'SHOW_MODAL',
  LAST_ID: 'LAST_ID',
  LAST_Z_INDEX: 'LAST_Z_INDEX'
};
var T_MODAL_POSITION;
(function (T_MODAL_POSITION) {
  T_MODAL_POSITION["LEFT"] = "left";
  T_MODAL_POSITION["RIGHT"] = "right";
  T_MODAL_POSITION["TOP"] = "top";
  T_MODAL_POSITION["BOTTOM"] = "bottom";
  T_MODAL_POSITION["CENTER"] = "center";
})(T_MODAL_POSITION || (T_MODAL_POSITION = {}));
var T_DEFAULT_VALUES;
(function (T_DEFAULT_VALUES) {
  T_DEFAULT_VALUES["ALL_OF_THEM"] = "ALL_OF_THEM";
  T_DEFAULT_VALUES["OVERRIDE"] = "OVERRIDE";
})(T_DEFAULT_VALUES || (T_DEFAULT_VALUES = {}));
var T_DEVICE_TYPE;
(function (T_DEVICE_TYPE) {
  T_DEVICE_TYPE["MOBILE"] = "mobile";
  T_DEVICE_TYPE["TABLET"] = "tablet";
  T_DEVICE_TYPE["DESKTOP"] = "desktop";
  T_DEVICE_TYPE["CENTER"] = "center";
})(T_DEVICE_TYPE || (T_DEVICE_TYPE = {}));

// Angular
/**
 * Export class SimpleModalComponent
 */
class SimpleModalComponent {
  /**
   * Constructor
   * @param {SimpleModalBroadcaster} broadCaster
   * @param {SimpleModalHelper} helper
   * @param {ChangeDetectorRef} changeDetector
   * @param {ViewportService} vp
   * @param {LocalStorageService} localStorage
   * @param {any} window
   * @param {LockScrollService} scrollService
   */
  constructor(broadCaster, helper, changeDetector, scrollService, vp, localStorage, window) {
    this.broadCaster = broadCaster;
    this.helper = helper;
    this.changeDetector = changeDetector;
    this.scrollService = scrollService;
    this.vp = vp;
    this.localStorage = localStorage;
    this.window = window;
    /**
     * Flag: Current modal status hide|show
     * @type {boolean}
     */
    this.isHidden = false;
    /**
     * Overlay with a shade
     * @type {boolean}
     */
    this.isShade = true;
    /**
     * Position
     * @type T_MODAL_POSITION
     */
    this.position = T_MODAL_POSITION.CENTER;
    /**
     * Subscriptions
     * @type {Subscription}
     */
    this.subscriptions = new Subscription();
    /**
     * Simple modal config data
     * @type {any}
     */
    this.config = {
      // Point to detect device type by screen width (MAX value)
      resolution: {
        mobile: 640,
        // Mobile (mav value in px)
        tablet: 970 // Tablet (mav value in px)
      },
      /**
       * Default values if we did't received needed data
       */
      defValues: {
        mobile: {
          width: 80,
          height: 20
        },
        tablet: {
          width: 50,
          height: 15
        },
        desktop: {
          width: 30,
          height: 10
        },
        // Center position
        center: {
          width: 100,
          height: 100
        },
        isPercent: true // Use a percent to calculate dimensions
      }
    };
    this._window = this.window;
  }
  // Start listen keypress
  keyup(event) {
    // I did replace "keyCode", cause it's deprecated now
    if ((this.helper.dispatchForCode(event) + '').toLowerCase() === 'escape') {
      const data = {
        id: this.modalId
      };
      this.broadCaster.emit(SimpleModalKeys.CLOSE_MODAL, data);
    }
  }
  /**
   * ngOnInit
   */
  ngAfterViewInit() {
    // Lock screen
    this.scrollService.lockScroll();
    // Create and render component
    this.refs = this.helper.createComponent(this.execute.component, this.execute.data, this.container);
    // Initialize subscriptions
    this.initSubscriptions();
    // Initialize needed data
    this.initData();
  }
  /**
   * Initialize needed data
   */
  initData() {
    // Define position object
    const lastZIndex = this.localStorage.getItem(SimpleModalKeys.LAST_Z_INDEX) || 0;
    this.parentCss = {
      zIndex: 1000 + parseInt(lastZIndex, 10) + 1
    };
    this.childCss = {};
    // Keep in localstorage last zIndex
    this.localStorage.setItem(SimpleModalKeys.LAST_Z_INDEX, lastZIndex);
    // Define screen width
    const screenWidth = this._window.innerWidth;
    // Define screen height
    const screenHeight = this._window.innerHeight;
    // Get dimension value for the width or height
    const getDimensionValue = fieldName => {
      const obj = this.dimensions && this.dimensions[deviceType] ? this.dimensions[deviceType] : this.config.defValues[deviceType];
      return obj ? obj[fieldName] : undefined;
    };
    const isPercent = !(this.dimensions && this.dimensions.isPercent === false);
    // Get pixel value for the width and height
    const getPxValue = (org, value) => {
      return isPercent ? value : org * value / 100;
    };
    // Define needed variables
    let deviceType;
    const cssObj = {};
    // Define device type
    if (this.position === T_MODAL_POSITION.CENTER) {
      deviceType = T_DEVICE_TYPE.CENTER;
    } else {
      deviceType = screenWidth <= this.config.resolution.mobile ? T_DEVICE_TYPE.MOBILE : screenWidth <= this.config.resolution.tablet ? T_DEVICE_TYPE.TABLET : T_DEVICE_TYPE.DESKTOP;
    }
    if (this.position === T_MODAL_POSITION.CENTER) {
      cssObj.width = getDimensionValue('width');
      cssObj.height = getDimensionValue('height');
    } else {
      if (this.position === T_MODAL_POSITION.RIGHT || this.position === T_MODAL_POSITION.LEFT) {
        cssObj.height = !isPercent ? screenWidth : 100;
        cssObj.width = getDimensionValue('width');
      }
      if (this.position === T_MODAL_POSITION.TOP || this.position === T_MODAL_POSITION.BOTTOM) {
        cssObj.width = !isPercent ? screenWidth : 100;
        cssObj.height = getDimensionValue('height');
      }
    }
    if (this.position === T_MODAL_POSITION.CENTER) {
      const getPosition = (field, org) => {
        return ((isPercent ? 100 : org) - getDimensionValue(field)) / 2;
      };
      cssObj.top = getPxValue(screenHeight, getPosition('height', screenHeight));
      cssObj.left = getPxValue(screenWidth, getPosition('width', screenWidth));
    }
    if (this.position === T_MODAL_POSITION.RIGHT || this.position === T_MODAL_POSITION.LEFT) {
      cssObj[this.position === T_MODAL_POSITION.RIGHT ? 'right' : 'left'] = 0;
    }
    if (this.position === T_MODAL_POSITION.BOTTOM || this.position === T_MODAL_POSITION.TOP) {
      cssObj[this.position === T_MODAL_POSITION.BOTTOM ? 'bottom' : 'top'] = 0;
    }
    // Loop needed styles to apply it to the "modal-content"
    ['width', 'height', 'top', 'left', 'right', 'bottom', 'z-index'].forEach(field => {
      if (cssObj.hasOwnProperty(field)) {
        if (field === 'z-index') {
          this.parentCss.zIndex = cssObj[field];
        } else {
          // Apply css styles
          this.childCss[field] = `${cssObj[field]}${field === 'width' || field === 'height' ? isPercent ? '%' : 'px' : ''}`;
        }
      }
    });
    // Detect new changes in entire component
    this.changeDetector.detectChanges();
  }
  /**
   * Initialize Subscriptions
   */
  initSubscriptions() {
    // If we need to hide modal
    this.subscriptions.add(this.broadCaster.on(SimpleModalKeys.HIDE_MODAL).subscribe(val => {
      if (val.exceptId === T_DEFAULT_VALUES.ALL_OF_THEM) {
        this.isHidden = true;
      } else {
        if (this.modalId !== val.exceptId) {
          this.isHidden = true;
        }
      }
    }));
    // If we need to show modal
    this.subscriptions.add(this.broadCaster.on(SimpleModalKeys.SHOW_MODAL).subscribe(val => {
      if (val.id === T_DEFAULT_VALUES.ALL_OF_THEM) {
        this.isHidden = false;
      } else {
        if (this.modalId !== val.id) {
          this.isHidden = false;
        }
      }
    }));
    this.subscriptions.add(this.vp.viewport$.subscribe(() => {
      this.initData();
    }));
  }
  /**
   * ngOnDesytroy
   */
  ngOnDestroy() {
    this.scrollService.unlockScroll();
    this.subscriptions.unsubscribe();
  }
  static {
    this.ɵfac = function SimpleModalComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SimpleModalComponent)(i0.ɵɵdirectiveInject(SimpleModalBroadcaster), i0.ɵɵdirectiveInject(SimpleModalHelper), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i3.LockScrollService), i0.ɵɵdirectiveInject(i4.ViewportService), i0.ɵɵdirectiveInject(i5.LocalStorageService), i0.ɵɵdirectiveInject(WINDOW));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: SimpleModalComponent,
      selectors: [["upr-simple-modal"]],
      viewQuery: function SimpleModalComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 5, ViewContainerRef);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.container = _t.first);
        }
      },
      hostBindings: function SimpleModalComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("keyup", function SimpleModalComponent_keyup_HostBindingHandler($event) {
            return ctx.keyup($event);
          }, false, i0.ɵɵresolveDocument);
        }
      },
      standalone: false,
      decls: 1,
      vars: 1,
      consts: [["container", ""], ["id", "simple-modal", 3, "ngStyle", 4, "ngIf"], ["id", "simple-modal", 3, "ngStyle"], [1, "simple-modal-body", 3, "ngStyle"], ["class", "simple-modal-shade", 4, "ngIf"], [1, "simple-modal-shade"]],
      template: function SimpleModalComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵtemplate(0, SimpleModalComponent_div_0_Template, 5, 3, "div", 1);
        }
        if (rf & 2) {
          i0.ɵɵproperty("ngIf", !ctx.isHidden);
        }
      },
      dependencies: [i6.NgIf, i6.NgStyle],
      styles: [".outline-style-primary-blue[_ngcontent-%COMP%]{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white[_ngcontent-%COMP%]{outline:1px dashed #fff;outline-offset:2px}.font-12[_ngcontent-%COMP%]{font-size:12px!important}.font-14[_ngcontent-%COMP%]{font-size:14px!important}.font-16[_ngcontent-%COMP%]{font-size:16px!important}.font-18[_ngcontent-%COMP%]{font-size:18px!important}.font-20[_ngcontent-%COMP%]{font-size:20px!important}.font-22[_ngcontent-%COMP%]{font-size:22px!important}.font-24[_ngcontent-%COMP%]{font-size:24px!important}.font-26[_ngcontent-%COMP%]{font-size:26px!important}.font-28[_ngcontent-%COMP%]{font-size:28px!important}.font-30[_ngcontent-%COMP%]{font-size:30px!important}.font-32[_ngcontent-%COMP%]{font-size:32px!important}.fade-me-in[_ngcontent-%COMP%]{animation:_ngcontent-%COMP%_fadeIn .25s;-webkit-animation:_ngcontent-%COMP%_fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes _ngcontent-%COMP%_fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes _ngcontent-%COMP%_fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes _ngcontent-%COMP%_fadeIn{0%{opacity:0}to{opacity:1}}#simple-modal[_ngcontent-%COMP%]{position:fixed;width:100%;height:100%;top:0;bottom:0;z-index:999}.simple-modal-shade[_ngcontent-%COMP%]{position:absolute;top:0;bottom:0;width:100%;height:100%;background:#333;opacity:.6}.simple-modal-body[_ngcontent-%COMP%]{position:absolute;width:auto;height:auto;z-index:1000;overflow:auto}"]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SimpleModalComponent, [{
    type: Component,
    args: [{
      selector: 'upr-simple-modal',
      standalone: false,
      template: "<div id=\"simple-modal\" [ngStyle]=\"parentCss\" *ngIf=\"!isHidden\">\r\n\t<div class=\"simple-modal-body\" [ngStyle]=\"childCss\">\r\n\t\t<span #container></span>\r\n\t</div>\r\n\t<div *ngIf=\"!isHidden && isShade\" class=\"simple-modal-shade\"></div>\r\n</div>\r\n",
      styles: [".outline-style-primary-blue{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}#simple-modal{position:fixed;width:100%;height:100%;top:0;bottom:0;z-index:999}.simple-modal-shade{position:absolute;top:0;bottom:0;width:100%;height:100%;background:#333;opacity:.6}.simple-modal-body{position:absolute;width:auto;height:auto;z-index:1000;overflow:auto}\n"]
    }]
  }], () => [{
    type: SimpleModalBroadcaster
  }, {
    type: SimpleModalHelper
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: i3.LockScrollService
  }, {
    type: i4.ViewportService
  }, {
    type: i5.LocalStorageService
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }], {
    container: [{
      type: ViewChild,
      args: ['container', {
        read: ViewContainerRef
      }]
    }],
    keyup: [{
      type: HostListener,
      args: ['document:keyup', ['$event']]
    }]
  });
})();

// Angular
class SimpleModalModule {
  static {
    this.ɵfac = function SimpleModalModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SimpleModalModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: SimpleModalModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [SimpleModalBroadcaster, SimpleModalHelper],
      imports: [CommonModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SimpleModalModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule],
      declarations: [SimpleModalComponent],
      exports: [SimpleModalComponent],
      providers: [SimpleModalBroadcaster, SimpleModalHelper]
    }]
  }], null, null);
})();

// Angular
/**
 * Export SimpleModalService
 */
class SimpleModalService {
  /**
   * Constructor
   * @param {SimpleModalHelper} helper
   * @param {SimpleModalBroadcaster} broadCaster
   * @param {LocalStorageService} localStorage
   */
  constructor(helper, broadCaster, localStorage) {
    this.helper = helper;
    this.broadCaster = broadCaster;
    this.localStorage = localStorage;
    this.simpleModal$ = new BehaviorSubject(null);
    /**
     * Reference to the modals
     * @type any
     */
    this.refs = {};
  }
  /**
   * Show the modal
   * 			=> sm.show(false|true) - gonna close all modals
   * 			=> sm.show('ComponentNameId') - gonna show a modal with id "ComponentNameId"
   * 			=> sm.show() - gonna show the last opened modal
   * @param {string|boolean} id
   */
  show(id) {
    if (id) {
      if (typeof id === 'string') {
        this.setLastId(id);
      }
    } else {
      id = this.getLastId();
    }
    this.hide(T_DEFAULT_VALUES.ALL_OF_THEM);
    const data = {
      id: !id || typeof id === 'boolean' ? T_DEFAULT_VALUES.ALL_OF_THEM : id
    };
    this.broadCaster.emit(SimpleModalKeys.SHOW_MODAL, data);
  }
  /**
   * Hide the modal
   * 			=> sm.hide(false|true) - gonna hide all existing modals
   * 			=> sm.hide('ComponentNameId')	- gonna hide a modal with id "ComponentNameId"
   * 			=> sm.hide() - gonna hide the last opened modal
   * @param {string|boolean} id
   */
  hide(id) {
    if (id) {
      if (typeof id === 'string') {
        this.setLastId(id);
      }
    } else {
      id = this.getLastId();
    }
    const data = {
      exceptId: !id || typeof id === 'boolean' ? T_DEFAULT_VALUES.ALL_OF_THEM : id
    };
    this.broadCaster.emit(SimpleModalKeys.HIDE_MODAL, data);
  }
  /**
   * Close the modal
   * 			=> sm.close(false|true) - gonna close all existing modals
   * 			=> sm.close('ComponentNameId') - gonna close a modal with id "ComponentNameId"
   * @param {string} id
   */
  close(id) {
    if (!id || typeof id !== 'boolean') {
      id = this.getLastId();
    }
    this.destroyModal({
      id: !id || typeof id === 'boolean' ? T_DEFAULT_VALUES.ALL_OF_THEM : id
    });
  }
  /**
   * Open modal
   * 			=> sm.open('ComponentNameId', data, options) - gonna open a new modal with a "ComponentNameId" in inside (component name is a string)
   * 			=> sm.open(Component, data, options) - gonna open a new modal with a "Component" in inside (component is an instance)
   * 			=> sm.open(Component, data, {
   * 						position: T_MODAL_POSITION.TOP // the modal position would be in a top of a page
   * 						// If a position different then center, it would be good to specify dimensions for the device types,
   * 								otherwise SimpleModal gonna use default values from config itself
   * 					  dimensions: {
   * 					    	mobile: {height: 30}, // Modal width for the mobile devices
                                      tablet: {height: 25}, // Modal width for the tablet devices
                                      desktop: {width: 100, height: 20}, // Modal width for the desktop devices
                                      isPercent: true // Default value. Gonna calculate dimensions in percent, otherwise in pixel
   * 					  },
   * 					  isShade: true // gonna display the shade for the modal
   * 				})
   * @param {any} component
   * @param {any} data
   * @param {IModalParams} params
   * @return {string}
   */
  open(component, data, params) {
    if (data) {
      this.simpleModal$.next(data);
    }
    if (this.helper.sizeOf(this.refs) < 1) {
      this.localStorage.setItem(SimpleModalKeys.LAST_ID, null);
      this.subscriptions = new Subscription();
      this.subscriptions.add(this.broadCaster.on(SimpleModalKeys.CLOSE_MODAL).subscribe(_val => {
        this.destroyModal(_val);
      }));
    }
    return this.createModal(component, data, params);
  }
  /**
   * Set last used id
   * @param {string} id
   * @param {boolean} exclude
   */
  setLastId(id, exclude = false) {
    const obj = {};
    let cacheData = this.localStorage.getItem(SimpleModalKeys.LAST_ID);
    if (cacheData) {
      cacheData = JSON.parse(cacheData);
    }
    if (cacheData) {
      for (const x in cacheData) {
        if (cacheData.hasOwnProperty(x) && x !== id) {
          obj[x] = x;
        }
      }
    }
    if (!exclude) {
      obj[id] = id;
    }
    if (this.helper.sizeOf(obj) > 0) {
      this.localStorage.setItem(SimpleModalKeys.LAST_ID, obj);
    }
    return {
      id: id
    };
  }
  /**
   * Get last used modal ID
   * @return {string}
   */
  getLastId() {
    let id;
    let cacheData = this.localStorage.getItem(SimpleModalKeys.LAST_ID);
    if (cacheData) {
      cacheData = JSON.parse(cacheData);
    }
    if (cacheData) {
      const keys = Object.keys(cacheData);
      if (keys) {
        id = keys[keys.length - 1];
      }
    }
    return id ? id : '';
  }
  /**
   * Create a new <simple-modal> component in a DOM
   */
  createModal(component, data, params) {
    // Get modalId
    const modalData = this.setLastId(this.helper.getId(component));
    // Define modal properties
    const serviceData = {
      modalId: modalData.id,
      execute: {
        component: component,
        data: data
      }
    };
    // Create and render component
    this.refs[modalData.id] = this.helper.createComponent(SimpleModalComponent, this.helper.merge(serviceData, params));
    // Return new generated ID
    return modalData.id;
  }
  /**
   * Destroy modal
   * @param {ICloseModal} data
   */
  destroyModal(data) {
    // If we need to destroy all modals
    if (data.id === T_DEFAULT_VALUES.ALL_OF_THEM) {
      // Destroy all existing modals
      for (const x in this.refs) {
        if (this.refs.hasOwnProperty(x)) {
          this.refs[x].destroy();
          this.setLastId(data.id, true);
        }
      }
      // Need to destroy just specific modal
    } else {
      this.refs[data.id].destroy();
      this.setLastId(data.id, true);
    }
    // If we don't have any other existing modals -> unsubscribe from subscriptions
    if (this.helper.sizeOf(this.refs) < 1) {
      this.subscriptions.unsubscribe();
      // Reset last z-index if we got no other modals
      this.localStorage.setItem(SimpleModalKeys.LAST_Z_INDEX, 0);
    }
  }
  static {
    this.ɵfac = function SimpleModalService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SimpleModalService)(i0.ɵɵinject(SimpleModalHelper), i0.ɵɵinject(SimpleModalBroadcaster), i0.ɵɵinject(i5.LocalStorageService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: SimpleModalService,
      factory: SimpleModalService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SimpleModalService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: SimpleModalHelper
  }, {
    type: SimpleModalBroadcaster
  }, {
    type: i5.LocalStorageService
  }], null);
})();

/*
 * Public API Surface of simple-modal
 */

/**
 * Generated bundle index. Do not edit.
 */

export { SimpleModalComponent, SimpleModalModule, SimpleModalService, T_DEFAULT_VALUES, T_DEVICE_TYPE, T_MODAL_POSITION };
